/**
 * Map Canvas Component
 *
 * Google Maps wrapper component.
 * Manages map instance, markers, and info windows.
 * Subscribes to locationStore and mapViewStore for reactive updates.
 * Uses Tailwind CSS for styling (light DOM rendering).
 *
 * @element peach-map-canvas
 *
 * @fires map-ready - Emitted when map is initialized
 * @fires location-selected - Emitted when a marker is clicked (matches list/calendar events)
 *
 * @example
 * ```html
 * <peach-map-canvas apiKey="YOUR_GOOGLE_MAPS_API_KEY"></peach-map-canvas>
 * ```
 */

/// <reference types="@types/google.maps" />

import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { locationStore } from "@/features/locations/stores/location-store";
import { mapViewStore } from "@/features/map/stores/map-view-store";
import { uiStore } from "@/stores/ui-store";
import {
  getCurrentTheme,
  getCSSVariable,
  getMapStyles,
  applyInfoWindowStyling,
} from "@/features/map/services/map-theme-service";
import type { Location } from "@/types/location";
import type { MapMarker } from "@/types/map";
import { getStopStatusDisplay } from "@/utils/stop-status";
import {
  getLocationAvailability,
  isStopPast,
} from "@/utils/location-availability";
import mapPinPeachUrl from "/map-pin-peach.svg?url";
import mapPinPeachGrayUrl from "/map-pin-peach-gray.svg?url";

@customElement("peach-map-canvas")
export class MapCanvasComponent extends LitElement {
  /**
   * Render in light DOM to enable Tailwind CSS classes
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Google Maps API key (optional if loaded globally)
   */
  @property({ type: String })
  declare apiKey: string;

  /**
   * Map instance
   */
  @state()
  declare private map?: google.maps.Map;

  /**
   * Map markers
   */
  @state()
  declare private markers: MapMarker[];

  /**
   * Current info window
   */
  @state()
  declare private infoWindow?: google.maps.InfoWindow;

  /**
   * Visible locations from store
   */
  @state()
  declare private visibleLocations: Location[];

  /**
   * Store unsubscribe function
   */
  private unsubscribe?: () => void;

  /**
   * Whether Google Maps API is loaded
   */
  private isGoogleMapsLoaded: boolean;

  /**
   * Detect if running in test environment (e.g., Playwright)
   * Animations are disabled in tests for faster execution
   */
  private isTestEnvironment(): boolean {
    return typeof navigator !== 'undefined' && navigator.webdriver === true;
  }

  /**
   * Bound event handler for theme changes (for cleanup)
   */
  private handleThemeChange: (event: Event) => void;

  /**
   * Currently displayed info window location (for theme refresh)
   */
  private currentInfoLocation?: Location;

  /**
   * Debounce timer for bounds updates
   */
  private boundsUpdateTimer?: number;

  /**
   * Whether bounds updates should be enabled (disabled during initial load)
   */
  private enableBoundsUpdates: boolean;

  /**
   * Flag to track programmatic map operations (centering on selection)
   * When true, prevents bounds updates from triggering location filtering
   */
  private isProgrammaticOperation: boolean;

  constructor() {
    super();
    this.apiKey = "";
    this.markers = [];
    this.visibleLocations = [];
    this.isGoogleMapsLoaded = false;
    this.enableBoundsUpdates = false;
    this.isProgrammaticOperation = false;

    this.handleThemeChange = this.refreshMarkerColors.bind(this);
  }

  /**
   * Apply map center from search
   *
   * @remarks
   * Called when a search sets a new center point.
   * Clears the center after applying to prevent re-centering on subsequent updates.
   */
  /**
   * Check if this canvas is effectively visible
   *
   * @remarks
   * Returns false for elements hidden via display:none (no dimensions)
   * or opacity:0/pointer-events:none (mobile overlay when hidden).
   */
  private isCanvasVisible(): boolean {
    // Check for display:none (element has no dimensions)
    if (this.offsetWidth === 0 || this.offsetHeight === 0) return false;

    // Check parent container for opacity:0 (mobile overlay when hidden)
    const parent = this.closest("[aria-hidden]");
    if (parent?.getAttribute("aria-hidden") === "true") return false;

    return true;
  }

  private applySearchCenter(center: { lat: number; lng: number; zoom?: number }) {
    const debugId = `${this.offsetWidth}x${this.offsetHeight}`;

    if (!this.map) {
      console.log(`[MapCanvas:${debugId}] applySearchCenter: no map instance`);
      return;
    }

    // Skip if this canvas is not visible - let the visible one handle it
    if (!this.isCanvasVisible()) {
      console.log(`[MapCanvas:${debugId}] applySearchCenter: not visible`);
      return;
    }

    console.log(`[MapCanvas:${debugId}] applySearchCenter:`, center);
    this.centerOnCoordinates(center);
    mapViewStore.getState().setMapCenter(null);
  }

  /**
   * Handle selected location changes
   */
  private handleSelectedLocationChange(
    selectedLocationId: number | null,
    prevSelectedLocationId: number | null,
  ): number | null {
    if (selectedLocationId === null || !this.map) {
      return prevSelectedLocationId;
    }

    const locationChanged = selectedLocationId !== prevSelectedLocationId;
    this.centerOnLocation(selectedLocationId, !locationChanged);
    return selectedLocationId;
  }

  /**
   * Handle map bounds changes (pan/zoom)
   *
   * @remarks
   * Debounced to 300ms to avoid excessive filtering during pan/zoom.
   * Updates locationStore with current viewport bounds for location filtering.
   * Disabled during initial map load and programmatic operations (selection centering).
   */
  private handleMapBoundsChange() {
    if (!this.map || !this.enableBoundsUpdates || this.isProgrammaticOperation) return;

    clearTimeout(this.boundsUpdateTimer);

    this.boundsUpdateTimer = window.setTimeout(() => {
      if (!this.map || this.isProgrammaticOperation) return;

      const bounds = this.map.getBounds();
      if (!bounds) return;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      locationStore.getState().setMapBounds({
        north: ne.lat(),
        south: sw.lat(),
        east: ne.lng(),
        west: sw.lng(),
      });
    }, 300);
  }

  /**
   * Detect if visible location IDs have changed
   *
   * @remarks
   * Pure function - no side effects. Compares Set sizes and contents.
   */
  private hasVisibleLocationsChanged(
    current: Set<number>,
    previous: Set<number>,
  ): boolean {
    if (current.size !== previous.size) return true;
    return Array.from(current).some((id) => !previous.has(id));
  }

  /**
   * Detect if search center has changed
   *
   * @remarks
   * Pure function - no side effects. Checks for new mapCenter value.
   */
  private hasSearchCenterChanged(
    current: { lat: number; lng: number; zoom?: number } | null,
    previous: { lat: number; lng: number; zoom?: number } | null,
  ): boolean {
    return current !== null && current !== previous;
  }

  /**
   * Detect if this is a new search
   *
   * @remarks
   * Pure function - no side effects. Checks if searchPoint changed from previous value.
   */
  private isNewSearch(
    current: { lat: number; lng: number } | null,
    previous: { lat: number; lng: number } | null,
  ): boolean {
    return current !== previous && current !== null;
  }

  /**
   * Handle search center change side effect
   *
   * @remarks
   * Updates map position from geocoding search.
   * Returns null to signal that prevMapCenter should be reset.
   */
  private handleSearchCenterChange(center: {
    lat: number;
    lng: number;
    zoom?: number;
  }): null {
    this.applySearchCenter(center);
    return null;
  }

  /**
   * Pending marker update request ID (for cancellation)
   */
  private pendingMarkerUpdate: number | null = null;

  /**
   * Handle visible locations change side effect
   *
   * @remarks
   * Schedules marker update asynchronously using requestIdleCallback
   * (with setTimeout fallback) so the locations list can render immediately.
   * Returns new Set to track for next comparison.
   */
  private handleVisibleLocationsChange(
    currentVisibleIds: Set<number>,
  ): Set<number> {
    if (!this.map) return currentVisibleIds;

    // Cancel any pending marker update
    if (this.pendingMarkerUpdate !== null) {
      if (typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(this.pendingMarkerUpdate);
      } else {
        clearTimeout(this.pendingMarkerUpdate);
      }
    }

    // Schedule marker update asynchronously so list renders first
    if (typeof requestIdleCallback === 'function') {
      this.pendingMarkerUpdate = requestIdleCallback(() => {
        this.pendingMarkerUpdate = null;
        this.updateMarkers();
      }, { timeout: 100 }); // Ensure it runs within 100ms
    } else {
      // Fallback for Safari
      this.pendingMarkerUpdate = setTimeout(() => {
        this.pendingMarkerUpdate = null;
        this.updateMarkers();
      }, 0) as unknown as number;
    }

    return new Set(currentVisibleIds);
  }

  /**
   * Handle bounds fitting side effect
   *
   * @remarks
   * Auto-fits map bounds for new searches only.
   * Requires markers to be updated first.
   */
  private handleBoundsFitting(shouldFitBounds: boolean): void {
    if (!shouldFitBounds) return;
    if (this.markers.length === 0) return;

    this.fitBounds();
  }

  /**
   * Store unsubscribe functions for cleanup
   */
  private unsubscribeMapView?: () => void;
  private unsubscribeUi?: () => void;

  /**
   * Subscribe to stores for reactive updates
   *
   * @remarks
   * Subscribes to both locationStore and mapViewStore:
   * - locationStore: visibleLocationIds, selectedLocationId, searchPoint
   * - mapViewStore: mapCenter
   * Delegates to pure functions for state change detection and focused
   * handler functions for side effects.
   */
  private subscribeToMapStore() {
    let prevSelectedLocationId: number | null = null;
    let prevVisibleLocationIds: Set<number> = new Set();
    let prevSearchPoint: { lat: number; lng: number } | null = null;
    let prevMapCenter: { lat: number; lng: number; zoom?: number } | null = null;

    // Subscribe to mapViewStore for map center changes
    this.unsubscribeMapView = mapViewStore.subscribe((state) => {
      // Handle search center changes (explicit action)
      if (this.hasSearchCenterChanged(state.mapCenter, prevMapCenter)) {
        prevMapCenter = this.handleSearchCenterChange(state.mapCenter!);
      } else {
        prevMapCenter = state.mapCenter;
      }
    });

    // Subscribe to uiStore for mobile map overlay visibility
    let prevShowMapOverlay = uiStore.getState().showMapOverlay;
    this.unsubscribeUi = uiStore.subscribe(
      (state) => state.showMapOverlay,
      (showMapOverlay) => {
        const becameVisible = showMapOverlay && !prevShowMapOverlay;
        prevShowMapOverlay = showMapOverlay;

        if (!becameVisible || !this.map) return;

        // On mobile, trigger resize and refit bounds when overlay becomes visible
        const isMobile = window.innerWidth < 768;
        if (!isMobile) return;

        // Trigger resize so Google Maps recalculates container dimensions
        google.maps.event.trigger(this.map, "resize");

        // Refit bounds after resize
        requestAnimationFrame(() => this.fitBounds());
      },
    );

    // Subscribe to locationStore for location data and selection
    this.unsubscribe = locationStore.subscribe((state) => {
      // 1. Check if visible locations changed
      const currentVisibleIds = state.visibleLocationIds;
      const locationsChanged = this.hasVisibleLocationsChanged(
        currentVisibleIds,
        prevVisibleLocationIds,
      );

      // 2. Check if this is a new search
      const isNewSearchResult = this.isNewSearch(
        state.searchPoint,
        prevSearchPoint,
      );

      // Update local visible locations cache
      this.visibleLocations = state.getVisibleLocations();

      // 3. Update markers when locations change
      if (locationsChanged) {
        prevVisibleLocationIds = this.handleVisibleLocationsChange(
          currentVisibleIds,
        );

        // 4. Auto-fit bounds for new searches only
        this.handleBoundsFitting(isNewSearchResult);
      }

      prevSearchPoint = state.searchPoint;

      // 5. Handle location selection (only when it changes)
      if (state.selectedLocationId !== prevSelectedLocationId) {
        prevSelectedLocationId = this.handleSelectedLocationChange(
          state.selectedLocationId,
          prevSelectedLocationId,
        );
      }
    });
  }

  /**
   * Set up theme change listener for dev mode
   */
  private setupDevThemeListener() {
    if (import.meta.env.DEV) {
      document.addEventListener("theme-changed", this.handleThemeChange);
    }
  }

  /**
   * Initialize map when component connects to DOM
   */
  connectedCallback() {
    super.connectedCallback();

    this.subscribeToMapStore();

    this.visibleLocations = locationStore.getState().getVisibleLocations();

    this.setupDevThemeListener();
  }

  /**
   * Initialize map after first render
   */
  async firstUpdated() {
    // Wait for render to complete and @query to resolve
    await this.updateComplete;
    this.initMap();
  }

  /**
   * Cleanup on disconnect
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribeMapView?.();
    this.unsubscribeUi?.();

    // Clear debounce timer
    clearTimeout(this.boundsUpdateTimer);

    // Cancel pending marker update
    if (this.pendingMarkerUpdate !== null) {
      if (typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(this.pendingMarkerUpdate);
      } else {
        clearTimeout(this.pendingMarkerUpdate);
      }
    }

    // Dev-only: Remove theme change event listener
    if (import.meta.env.DEV) {
      document.removeEventListener("theme-changed", this.handleThemeChange);
    }

    // Clean up markers
    this.markers.forEach((markerData) => {
      markerData.marker.setMap(null);
    });

    // Close info window
    if (this.infoWindow) {
      this.infoWindow.close();
    }
  }

  /**
   * Initialize Google Maps
   */
  private async initMap() {
    // Wait for Google Maps to be available
    if (typeof google === "undefined" || !google.maps) {
      console.warn("[MapCanvas] Google Maps not loaded yet, waiting...");
      // Retry after a short delay
      setTimeout(() => this.initMap(), 500);
      return;
    }

    // Query container directly (Light DOM rendering)
    const container = this.querySelector("#map-container") as HTMLElement;

    if (!container) {
      console.error("[MapCanvas] Map container not found");
      return;
    }

    this.isGoogleMapsLoaded = true;

    // Default center (Eastern US - shows all pickup locations)
    const defaultCenter = { lat: 38.5, lng: -85.0 };

    // Create map instance
    this.map = new google.maps.Map(container, {
      center: defaultCenter,
      zoom: 11,
      minZoom: 8,
      styles: getMapStyles(getCurrentTheme()),
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
      },
      disableDefaultUI: true,
      // Disable clickable icons for POIs, cities, neighborhoods, etc.
      // This prevents info windows from appearing for non-location markers
      clickableIcons: false,
    });

    // Wait for map tiles to load before hiding static placeholder
    google.maps.event.addListenerOnce(this.map, "tilesloaded", () => {
      const staticPlaceholder = document.getElementById("static-map-placeholder");
      if (staticPlaceholder) {
        staticPlaceholder.style.transition = "opacity 300ms ease-out";
        staticPlaceholder.style.opacity = "0";

        // Remove from DOM after fade completes
        setTimeout(() => {
          staticPlaceholder.remove();
        }, 300);
      }
    });

    // Listen for map bounds changes (pan/zoom) - debounced in handler
    // Use 'idle' event which fires after pan/zoom animation completes
    this.map.addListener("idle", () => {
      this.handleMapBoundsChange();
    });

    // Create info window with custom styling to disable default background
    this.infoWindow = new google.maps.InfoWindow({
      disableAutoPan: false,
    });

    // Add listener to inject custom CSS when InfoWindow opens
    // This overrides Google Maps' default white background container
    // Note: Google Maps InfoWindow API does not provide direct styling options.
    // The domready event manipulation is the standard approach for customizing
    // InfoWindow appearance (background, tail, close button styling).
    google.maps.event.addListener(this.infoWindow, "domready", () => {
      const theme = getCurrentTheme();
      const surfaceColor = getCSSVariable("--color-surface") || "#ffffff";
      applyInfoWindowStyling(theme, surfaceColor);
    });

    // RACE CONDITION FIX:
    // If a search was triggered via URL parameter (e.g., ?search=27606) before the map
    // finished initializing, the setMapCenter() call would have fired the subscription
    // but the centering would have been skipped (line 97: if (state.mapCenter && this.map)).
    // Now that the map is ready, check if there's a pending mapCenter and apply it.
    const mapViewState = mapViewStore.getState();
    if (mapViewState.mapCenter) {
      console.log(
        "[MapCanvas] Applying pending mapCenter after map initialization:",
        mapViewState.mapCenter,
      );
      this.centerOnCoordinates(mapViewState.mapCenter);
    }

    // Create initial markers
    this.updateMarkers();

    // Enable bounds updates after initial load is complete
    // This prevents marker re-creation during initial map setup
    this.enableBoundsUpdates = true;

    // Emit map ready event
    this.dispatchEvent(
      new CustomEvent("map-ready", {
        detail: { map: this.map },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Threshold for bulk marker updates - use batching when adding more than this many
   */
  private static readonly BULK_MARKER_THRESHOLD = 10;

  /**
   * Batch size for creating markers in chunks
   */
  private static readonly MARKER_BATCH_SIZE = 50;

  /**
   * Update markers based on visible locations
   *
   * @remarks
   * Only creates/removes markers. Does NOT handle map positioning.
   * Map positioning (fitBounds, centering) is handled separately in the subscription.
   *
   * Uses incremental updates to prevent flashing:
   * - Keeps existing markers for locations still visible
   * - Removes only markers for locations no longer visible
   * - Adds only markers for newly visible locations
   *
   * For bulk updates (>10 new markers), creates markers in batches using
   * setTimeout(0) to keep the UI responsive. Skips DROP animation on bulk
   * updates to further reduce overhead.
   */
  private updateMarkers() {
    if (!this.map) return;

    // Create lookup maps for efficient comparison
    const currentMarkersById = new Map(
      this.markers.map(m => [m.location.id, m])
    );
    const visibleLocationIds = new Set(
      this.visibleLocations.map(loc => loc.id)
    );

    // 1. Remove markers that are no longer visible
    this.markers.forEach((markerData) => {
      if (!visibleLocationIds.has(markerData.location.id)) {
        markerData.marker.setMap(null);
      }
    });

    // 2. Separate existing markers from locations needing new markers
    const existingMarkers: MapMarker[] = [];
    const locationsNeedingMarkers: Location[] = [];

    for (const location of this.visibleLocations) {
      const existingMarker = currentMarkersById.get(location.id);
      if (existingMarker) {
        existingMarkers.push(existingMarker);
      } else {
        locationsNeedingMarkers.push(location);
      }
    }

    const isBulkUpdate = locationsNeedingMarkers.length > MapCanvasComponent.BULK_MARKER_THRESHOLD;

    // 3. If not a bulk update, create markers synchronously (with animation)
    if (!isBulkUpdate) {
      const newMarkers = locationsNeedingMarkers.map((location) =>
        this.createMarker(location, true)
      );
      this.markers = [...existingMarkers, ...newMarkers];
      return;
    }

    // 4. For bulk updates, create markers in batches to keep UI responsive
    this.markers = existingMarkers;
    this.createMarkersBatched(locationsNeedingMarkers);
  }

  /**
   * Create a single marker for a location
   */
  private createMarker(location: Location, animate: boolean): MapMarker {
    const marker = new google.maps.Marker({
      position: { lat: location.lat, lng: location.lng },
      map: this.map,
      title: location.name,
      icon: this.getMarkerIcon(location),
      animation: animate ? google.maps.Animation.DROP : undefined,
    });

    marker.addListener("click", () => {
      this.handleMarkerClick(location, marker);
    });

    if (animate) {
      setTimeout(() => marker.setAnimation(null), 700);
    }

    return { location, marker, visible: true };
  }

  /**
   * Create markers in batches to prevent UI freeze
   *
   * @remarks
   * Uses setTimeout(0) between batches to yield to the main thread,
   * allowing the UI to remain responsive during bulk marker creation.
   */
  private createMarkersBatched(locations: Location[]) {
    const batchSize = MapCanvasComponent.MARKER_BATCH_SIZE;
    let index = 0;

    const processBatch = () => {
      const batch = locations.slice(index, index + batchSize);
      const newMarkers = batch.map((location) => this.createMarker(location, false));
      this.markers = [...this.markers, ...newMarkers];
      index += batchSize;

      if (index < locations.length) {
        setTimeout(processBatch, 0);
      }
    };

    processBatch();
  }

  /**
   * Refresh marker colors when theme changes
   */
  private refreshMarkerColors() {
    if (!this.map) return;

    console.log(
      "[MapCanvas] Theme changed, refreshing marker colors and map styles",
    );

    // Update map styles for the new theme
    this.map.setOptions({
      styles: getMapStyles(getCurrentTheme()),
    });

    // Update all markers with fresh colors from current theme
    this.markers.forEach((markerData) => {
      markerData.marker.setIcon(this.getMarkerIcon(markerData.location));
    });

    // If info window is currently open, refresh its content with new theme colors
    if (this.infoWindow && this.currentInfoLocation) {
      this.infoWindow.setContent(
        this.getInfoWindowContent(this.currentInfoLocation),
      );
    }
  }

  /**
   * Handle marker click
   */
  private handleMarkerClick(location: Location, marker: google.maps.Marker) {
    // Store current location for theme refresh
    this.currentInfoLocation = location;

    // Select location in store
    locationStore.getState().selectLocation(location.id);

    // Show info window
    if (this.infoWindow) {
      this.infoWindow.setContent(this.getInfoWindowContent(location));
      this.infoWindow.open(this.map, marker);
    }

    // Emit event (same event name as list items and calendar for consistent handling)
    this.dispatchEvent(
      new CustomEvent("location-selected", {
        detail: { location },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Calculate drawer widths for offset calculation
   */
  private getDrawerWidths(): { left: number; right: number } {
    const locationState = locationStore.getState();
    const uiState = uiStore.getState();

    // Left side can have TWO drawers stacked (locations list + location details)
    let leftDrawerWidth = 0;
    if (uiState.locationsDrawerOpen) {
      leftDrawerWidth += 384; // Locations list drawer
    }

    const hasLocationDetails =
      locationState.selectedLocationId !== null &&
      uiState.locationDetailsDrawerOpen;
    if (hasLocationDetails) {
      leftDrawerWidth += 384; // Location details drawer
    }

    // Right side has cart drawer (only on desktop)
    const rightDrawerWidth = uiState.cartOpen ? 384 : 0;

    return { left: leftDrawerWidth, right: rightDrawerWidth };
  }

  /**
   * Calculate longitude offset for map centering based on open drawers
   */
  private calculateLngOffset(netPixelOffset: number, zoom: number): number {
    const scale = Math.pow(2, zoom);
    const worldWidth = 256 * scale;
    const lngPerPixel = 360 / worldWidth;
    return netPixelOffset * lngPerPixel;
  }

  /**
   * Pan map to position with drawer offset on desktop
   */
  private panToWithDrawerOffset(
    position: google.maps.LatLng,
    zoom?: number,
  ) {
    if (!this.map) return;

    const isDesktop = window.innerWidth >= 768;
    if (!isDesktop) {
      this.map.panTo(position);
      return;
    }

    const { left, right } = this.getDrawerWidths();
    const netOffset = (left - right) / 2;

    if (netOffset === 0) {
      this.map.panTo(position);
      return;
    }

    const currentZoom = zoom ?? this.map.getZoom() ?? 12;
    const lngOffset = this.calculateLngOffset(netOffset, currentZoom);

    const offsetPosition = new google.maps.LatLng(
      position.lat(),
      position.lng() - lngOffset,
    );

    this.map.panTo(offsetPosition);
  }

  /**
   * Center map on a specific location
   * @param locationId - Location ID to center on
   * @param skipAnimation - If true, skip pan/zoom animation (for re-selection of same location)
   *
   * @remarks
   * Sets isProgrammaticOperation flag to prevent bounds updates from
   * triggering location filtering during programmatic zoom/pan.
   * Only manual pan/zoom should trigger bounds-based filtering.
   */
  private centerOnLocation(locationId: number, skipAnimation = false) {
    const markerData = this.markers.find((m) => m.location.id === locationId);
    if (!markerData || !this.map) return;

    const position = markerData.marker.getPosition();
    if (!position) return;

    // Only pan/zoom if this is a new location selection
    if (!skipAnimation) {
      // Mark as programmatic operation to prevent bounds filtering
      this.isProgrammaticOperation = true;

      this.panToWithDrawerOffset(position);
      this.map.setZoom(12);

      // Clear flag after animation + debounce completes (800ms = animation + 300ms debounce + buffer)
      setTimeout(() => {
        this.isProgrammaticOperation = false;
      }, 800);

      // Highlight marker with bounce animation (only for NEW selections)
      // Skip animations in test environment for faster test execution
      if (!this.isTestEnvironment()) {
        markerData.marker.setAnimation(google.maps.Animation.BOUNCE);
        setTimeout(() => {
          markerData.marker.setAnimation(null);
        }, 1400); // 2 bounces
      }
    }

    // Always show info window (even for re-selection)
    if (this.infoWindow) {
      // Store current location for theme refresh
      this.currentInfoLocation = markerData.location;

      this.infoWindow.setContent(
        this.getInfoWindowContent(markerData.location),
      );
      this.infoWindow.open(this.map, markerData.marker);
    }
  }

  /**
   * Center map on coordinates (from geocoding search)
   */
  private centerOnCoordinates(center: {
    lat: number;
    lng: number;
    zoom?: number;
  }) {
    if (!this.map) return;

    const position = new google.maps.LatLng(center.lat, center.lng);
    const { zoom } = center;

    // A search that omits zoom (e.g. a name search panning to the nearest
    // match) keeps whatever zoom is already in context rather than resetting
    // it. panToWithDrawerOffset falls back to the current zoom on its own.
    this.panToWithDrawerOffset(position, zoom);
    if (zoom !== undefined) {
      this.map.setZoom(zoom);
    }

    // Note: We DON'T clear mapCenter here anymore
    // It's now cleared in updateMarkers() after being used to skip fitBounds
    // This ensures the flag persists across multiple store updates during search
  }

  /**
   * Fit map bounds to show all markers
   */
  private fitBounds() {
    if (!this.map || this.markers.length === 0) return;

    const bounds = new google.maps.LatLngBounds();

    this.markers.forEach((markerData) => {
      const position = markerData.marker.getPosition();
      if (position) {
        bounds.extend(position);
      }
    });

    this.map.fitBounds(bounds);

    // Add padding - more on left for desktop drawer (384px = w-96)
    const isDesktop = window.innerWidth >= 768;
    const padding = isDesktop
      ? { top: 50, right: 50, bottom: 50, left: 434 } // 384px drawer + 50px padding
      : { top: 50, right: 50, bottom: 50, left: 50 };

    this.map.fitBounds(bounds, padding);
  }

  /**
   * Get custom marker icon based on location availability
   * Uses custom SVG icons:
   * - map-pin-peach.svg: locations with an available upcoming stop
   * - map-pin-peach-gray.svg: season-complete or sold-out locations (#314)
   *
   * @remarks
   * The location stays on the map so customers can see areas the truck
   * has served; the grey pin reactivates automatically if the feed adds
   * a new available stop.
   */
  private getMarkerIcon(location: Location): google.maps.Icon {
    const isUnavailable = getLocationAvailability(location) !== "active";
    const iconUrl = isUnavailable ? mapPinPeachGrayUrl : mapPinPeachUrl;

    // Icon size: 36x36 (matches SVG viewBox)
    // Anchor at bottom center for proper pin positioning
    const iconSize = new google.maps.Size(36, 36);
    const iconAnchor = new google.maps.Point(18, 36); // Center-bottom

    return {
      url: iconUrl,
      scaledSize: iconSize,
      anchor: iconAnchor,
    };
  }

  /**
   * Get info window HTML content
   * Reads actual computed color values from CSS variables to ensure
   * proper theming in Google Maps InfoWindow's isolated DOM context.
   */
  private getInfoWindowContent(location: Location): string {
    const { name, address, stops, type } = location;

    // Read actual computed color values from current theme
    // Google Maps InfoWindow creates an isolated DOM where CSS variables don't resolve
    const textColor = getCSSVariable("--color-text") || "#111827";
    const textMutedColor = getCSSVariable("--color-text-muted") || "#6b7280";
    const surfaceMutedColor =
      getCSSVariable("--color-surface-muted") || "#f9fafb";
    const borderColor = getCSSVariable("--color-border") || "#e5e7eb";
    const errorTextColor = getCSSVariable("--color-error-text") || "#991b1b";
    const warningTextColor =
      getCSSVariable("--color-warning-text") || "#78350f";
    const successTextColor =
      getCSSVariable("--color-success-text") || "#166534";

    const stopsList =
      stops.length > 0
        ? stops
            .slice(0, 3)
            .map((stop) => {
              const date = new Date(stop.date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              });

              // Past stops read as "Completed" rather than a live status
              if (isStopPast(stop)) {
                return `<li style="margin: 4px 0; color: ${textColor};">${date} - <span style="color: ${textMutedColor};">Completed</span></li>`;
              }

              const stopStatus = getStopStatusDisplay(stop);
              const statusColor = !stopStatus.isAvailable
                ? errorTextColor
                : stopStatus.text !== "Available"
                  ? warningTextColor
                  : successTextColor;
              const statusText = stopStatus.text;

              return `<li style="margin: 4px 0; color: ${textColor};">${date} - <span style="color: ${statusColor};">${statusText}</span></li>`;
            })
            .join("")
        : `<li style="color: ${textMutedColor};">No upcoming dates</li>`;

    const availability = getLocationAvailability(location);
    const availabilityNotice =
      availability === "active"
        ? ""
        : `<p style="margin: 8px 0 0; font-size: 13px; font-weight: 500; color: ${
            availability === "season-complete" ? textMutedColor : errorTextColor
          };">${
            availability === "season-complete"
              ? "This location has wrapped up for the season."
              : "Pickups at this location are sold out for the season."
          }</p>`;

    // Note: Background is now applied to .gm-style-iw-c outer container
    // Inner content div only needs padding, border-radius, and border for visual definition
    return `
      <div style="padding: 12px; min-width: 200px; border-radius: 8px; border: 1px solid ${borderColor};">
        <h3 style="margin: 0 0 8px; font-size: 16px; font-weight: 600; color: ${textColor};">${name}</h3>
        <p style="margin: 0 0 8px; color: ${textMutedColor}; font-size: 14px;">${address}</p>
        <div style="margin: 8px 0;">
          <span style="display: inline-block; padding: 4px 8px; background: ${surfaceMutedColor}; border: 1px solid ${borderColor}; border-radius: 4px; font-size: 12px; font-weight: 500; color: ${textColor};">
            ${type}
          </span>
        </div>
        <div style="margin-top: 12px;">
          <strong style="font-size: 13px; color: ${textColor};">${availability === "season-complete" ? "Past Dates:" : "Available Dates:"}</strong>
          <ul style="margin: 4px 0; padding-left: 20px; font-size: 13px; list-style-type: disc; color: ${textColor};">
            ${stopsList}
          </ul>
        </div>
        ${availabilityNotice}
      </div>
    `;
  }

  render() {
    return html`
      <div class="h-full w-full relative">
        <!-- Map Container -->
        <!-- z-index: 10 ensures static map placeholder (z-index: 15) stays visible during load -->
        <div id="map-container" class="h-full w-full" style="z-index: 10;">
          <!-- Prevent :empty CSS selector from hiding this container -->
        </div>

        <!-- Loading indicator overlay (centered spinner with localized background) -->
        ${
          !this.isGoogleMapsLoaded
            ? html`
              <div
                class="absolute inset-0 flex items-center justify-center pointer-events-none"
                style="z-index: 20;"
              >
                <div class="bg-black/50 rounded-lg p-6 shadow-lg">
                  <div class="text-center">
                    <div
                      class="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"
                    ></div>
                    <p class="text-sm text-white font-medium">Loading interactive map...</p>
                  </div>
                </div>
              </div>
            `
            : ""
        }
      </div>
    `;
  }
}

/**
 * TypeScript declaration for JSX/Liquid integration
 */
declare global {
  interface HTMLElementTagNameMap {
    "peach-map-canvas": MapCanvasComponent;
  }
}
